import React, { useEffect, useRef, useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useFormContext
} from 'react-hook-form';
import { useAddressLookup } from '../../hooks/useAddressLookup';
import { useDebounce } from '../../hooks/useDebounce';
import { useGetAddress } from '../../hooks/useGetAddress';
import {
  FetchifyConfig,
  FetchifyFindResult,
  FetchifyRetrieveResult
} from '../../services/fetchify';
import { PartialRecord } from '../../types/global';
import Button from '../Button';
import Lookup from '../Lookup';
import Select, { SelectProps } from '../Select';
import TextInput, { TextInputProps } from '../TextInput';
import './AddressLookup.scss';

interface BaseConfig {
  getValue?: (data: FetchifyRetrieveResult) => string;
}

export interface InputConfig extends Omit<TextInputProps, 'name'>, BaseConfig {
  inputType?: 'text';
}

export interface SelectConfig extends Omit<SelectProps<string>, 'name'>, BaseConfig {
  inputType?: 'select';
}

export type FieldConfig = InputConfig | SelectConfig;

export type FieldMap<T extends FieldValues = FieldValues> = PartialRecord<
  FieldPath<T>,
  FieldConfig
>;

interface AddressLookupProps<T> {
  fetchifyConfig: FetchifyConfig;
  fields: FieldMap<T>;
  enterAddressManuallyButtonText?: string;
  searchForAddressButtonText?: string;
  label?: string;
  placeholder?: string;
  tooltip?: string | React.ReactNode | React.ReactNode[];
  error?: string;
  onAddressSelect?: (address: FetchifyFindResult) => void;
  onAddressRetrieve?: (address: FetchifyRetrieveResult) => void;
  children?: ({
    isManualAddressActive
  }: {
    isManualAddressActive: boolean;
  }) => React.ReactNode | React.ReactNode[];
}

const AddressLookup = <T extends FieldValues = FieldValues>({
  fetchifyConfig,
  fields,
  enterAddressManuallyButtonText = 'Enter address manually',
  searchForAddressButtonText = 'Search for an address',
  label = 'Start typing address',
  placeholder = 'Enter address',
  tooltip,
  error,
  children,
  onAddressSelect,
  onAddressRetrieve
}: AddressLookupProps<T>) => {
  const lookupRef = useRef<HTMLSelectElement>();

  const { control, setValue, watch, clearErrors, resetField, setFocus } = useFormContext();

  const watchCurrentAddress = watch(Object.keys(fields));
  const hasAddressValues = watchCurrentAddress.some((value) => value);

  const [searchTerm, setSearchTerm] = useState('');
  const [isManualAddressActive, setIsManualAddressActive] = useState(hasAddressValues);

  const debouncedSearchTerm = useDebounce(searchTerm, 600);

  const focusFirstInput = () => {
    setTimeout(() => {
      setFocus(Object.keys(fields)[0]);
    }, 100);
  };

  const { data, isLoading } = useAddressLookup(debouncedSearchTerm, fetchifyConfig);
  const { mutate } = useGetAddress(fetchifyConfig, {
    onSuccess: (data) => {
      Object.keys(fields).forEach((name) => {
        resetField(name, { defaultValue: null });
        clearErrors(name);
        setValue(name, fields[name as FieldPath<T>]?.getValue?.(data));
        fields[name as FieldPath<T>]?.onChange?.(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fields[name as FieldPath<T>]?.getValue?.(data) as any
        );
        onAddressRetrieve?.(data);
      });

      setIsManualAddressActive(true);
      focusFirstInput();
    }
  });

  const getInput = (
    fieldConfig: FieldConfig,
    { value, name, onBlur, onChange, ref }: ControllerRenderProps<FieldValues, string>,
    { error }: ControllerFieldState
  ) => {
    switch (fieldConfig.inputType) {
      case 'select':
        return (
          <Select
            label={fieldConfig.label}
            onBlur={(evt) => {
              onBlur();
              fieldConfig.onBlur?.(evt);
            }}
            suggestions={fieldConfig.suggestions}
            onChange={(value) => {
              onChange(value);
              fieldConfig.onChange?.(value);
            }}
            name={name}
            data-testid={name}
            value={value || ''}
            error={error?.message}
            required={fieldConfig.required}
            ref={ref}
            pii={fieldConfig.pii}
          />
        );
      case 'text':
      default:
        return (
          <TextInput
            label={fieldConfig.label}
            onBlur={(evt) => {
              onBlur();
              fieldConfig.onBlur?.(evt);
            }}
            onChange={(value) => {
              onChange(value);
              fieldConfig.onChange?.(value);
            }}
            name={name}
            data-testid={name}
            value={value || ''}
            error={error?.message}
            required={fieldConfig.required}
            ref={ref}
            pii={fieldConfig.pii}
          />
        );
    }
  };

  useEffect(() => {
    if (error && lookupRef.current) {
      // @ts-expect-error not typed
      lookupRef.current?.element?.querySelector('input').focus();
    }
  }, [error]);

  return (
    <div className="address-lookup">
      {isManualAddressActive ? (
        <>
          <Button
            variant="link"
            className="search-for-address-button"
            onClick={() => {
              setIsManualAddressActive(false);

              Object.keys(fields).forEach((name) => {
                resetField(name, { defaultValue: null });
              });

              setTimeout(() => {
                if (lookupRef.current) {
                  // @ts-expect-error not typed
                  lookupRef.current?.element?.querySelector('input').focus();
                }
              }, 100);
            }}
          >
            {searchForAddressButtonText}
          </Button>

          {Object.keys(fields).map((name) => (
            <Controller
              key={name}
              render={({ field, fieldState }) => (
                <>{getInput(fields[name as FieldPath<T>], field, fieldState)}</>
              )}
              name={name}
              control={control}
            />
          ))}
        </>
      ) : (
        <>
          <Lookup
            label={label}
            placeholder={placeholder}
            suggestionsFetchRequested={({ value }: { value: string }) => setSearchTerm(value)}
            suggestions={data?.map((result) => ({
              value: result.id,
              label: result.labels[1]
            }))}
            onChange={(option) => {
              onAddressSelect?.(data.find((result) => result.id === option));
              mutate(option);
            }}
            name="searchTerm"
            data-testid="searchTerm"
            error={error}
            ref={lookupRef}
            isLoading={isLoading}
            required
            pii
            tooltip={tooltip}
            tooltipAriaLabel="More information on start typing address"
          />

          <Button
            variant="link"
            className="add-manually-button"
            onClick={() => {
              Object.keys(fields).forEach((name) => {
                clearErrors(name as FieldPath<T>);
              });

              setIsManualAddressActive(true);

              focusFirstInput();
            }}
          >
            {enterAddressManuallyButtonText}
          </Button>
        </>
      )}
      {children?.({ isManualAddressActive })}
    </div>
  );
};

export default AddressLookup;
